import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { subscribeGlobalConfig, subscribePosClientProfile, DEFAULT_GLOBAL_CONFIG } from '../utils/posClientUtil';
import { subscribePosClientConfig, subscribePosBranding, posFontStack } from '../utils/posUtil';
import { buildThemeCss, DEFAULT_THEME } from '../utils/themeUtil';
import { POS_VAT_MODE_NONE, POS_FONT_DEFAULT } from '../constants/posConst';
import { isPosCustomFontKey, posCustomFontId, loadPosCustomFont } from '../utils/posCustomFontUtil';
import { cacheLogoUrl } from '../utils/bookingUtils';

// Config for one POS screen, assembled from the two places it actually lives:
//
//   posGlobalConfig            settings shared by every client (currency, the
//                              idle timeout, the receipt's payment line)
//   posClients/<id>/profile    who this particular till belongs to
//   posTenants/<id>/config     what this client set for itself (its theme)
//
// They are merged in that order, so a club's own colours win over the
// deployment's and the deployment's win over the built-in defaults. A client
// that has never opened Config contributes nothing and simply inherits.
//
// The provider is mounted PER ROUTE rather than once around the app, and takes
// the client id as a prop. That is what lets /admin run on global config
// alone while /<client-id> runs on global config plus that client — with
// one `useConfig()` shape, so the till screens neither know nor care which of
// the two answered a given field.
//
// Field names are deliberately the ones the screens already used when this was
// a single-club app: a client's `name` surfaces as `courtName`, its `logoUrl`
// as `logoUrl`. formatMoney(value, config) and config?.courtName keep working
// untouched.

/**
 * What a screen sees before anything has loaded, and on a database with no
 * posGlobalConfig node at all — which is the normal state until an admin first
 * opens Config.
 */
const DEFAULT_CONFIG = {
  ...DEFAULT_GLOBAL_CONFIG,
  courtName: '',
  logoUrl: '',
  // No tax until an administrator says otherwise. Charging VAT nobody
  // configured is the one wrong default that takes money off a customer.
  vatMode: POS_VAT_MODE_NONE,
  vatRate: 0,
  // Theme — pushed into :root custom properties by the effect below. These are
  // the FALLBACKS; each client can override them under Config -> Theme, which
  // writes to its own posTenants/<id>/config. Shared with themeUtil so the
  // form's placeholders and the injected stylesheet cannot disagree about what
  // "unset" looks like.
  ...DEFAULT_THEME,
};

const ConfigContext = createContext(null);

/**
 * Supplies config to everything below it.
 *
 * @param {object} props
 * @param {string} [props.clientId] - when given, this client's profile is
 *   loaded and merged in. Omitted for screens that belong to no client
 *   (the welcome page, /admin).
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export function ConfigProvider({ clientId = null, children }) {
  const [globalConfig, setGlobalConfig] = useState(DEFAULT_GLOBAL_CONFIG);
  const [globalLoading, setGlobalLoading] = useState(true);
  // undefined = not loaded yet, null = no such client, object = found. The
  // three states are distinct because "not loaded" and "does not exist" call
  // for completely different screens.
  const [client, setClient] = useState(undefined);
  // This client's own settings. {} until loaded and for a client that has
  // never opened Config — an empty object merges to nothing, which is exactly
  // what "inherits everything" should look like.
  const [clientConfig, setClientConfig] = useState({});
  // The club's own logo and typeface. Separate from `clientConfig` because it
  // is a separate node: a logo is tens of kilobytes and must not be
  // re-downloaded every time a colour changes.
  const [branding, setBranding] = useState({});
  const [error, setError] = useState(null);
  // Bumped by reloadConfig() to tear the subscriptions down and open fresh
  // ones. Needed because onValue on an unreachable database never calls back
  // at all, so the only real retry is to re-subscribe.
  const [reloadKey, setReloadKey] = useState(0);

  // Bumped whenever the signed-in account CHANGES, which re-opens both
  // subscriptions below.
  //
  // This is not an optimisation. Every read here is gated on `auth != null`,
  // so signing out cancels these listeners with permission_denied — and a
  // cancelled Firebase listener is gone for good, even once the shared account
  // is signed back in a moment later. Without this, signing out of a till left
  // the screen holding data that could never refresh.
  const [authKey, setAuthKey] = useState(0);
  const lastUid = useRef(undefined);

  useEffect(() => onAuthStateChanged(auth, (user) => {
    const uid = user?.uid || null;
    // Only a real change counts. This fires once on mount with the current
    // user, and bumping on that would tear down the subscriptions the other
    // effects have just opened.
    if (lastUid.current === undefined) {
      lastUid.current = uid;
      return;
    }
    if (lastUid.current === uid) return;
    lastUid.current = uid;
    setAuthKey((key) => key + 1);
  }), []);

  /**
   * Re-runs the load from scratch. Wired to the connection-error screen's
   * Try Again; safe to call at any time.
   * @returns {void}
   */
  const reloadConfig = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    setError(null);
    const stop = subscribeGlobalConfig(
      (next) => {
        setGlobalConfig(next);
        setGlobalLoading(false);
      },
      (err) => {
        // Whatever was loaded stays loaded. Falling back to the defaults here
        // would swap a club's currency for the default one for as long as a
        // sign-out takes.
        setError(err.message);
        setGlobalLoading(false);
      },
    );
    return stop;
  }, [reloadKey, authKey]);

  useEffect(() => {
    if (!clientId) {
      setClient(null);
      return undefined;
    }
    // Only blanked when the CLIENT changes, not on a re-subscribe: going back
    // to `undefined` on an auth change would flash the loading spinner over a
    // till that already knows perfectly well which club it is.
    setClient((current) => (current?.id === clientId ? current : undefined));
    const stop = subscribePosClientProfile(
      clientId,
      setClient,
      // A failed read leaves the last known profile in place. `null` is
      // reserved for "this client does not exist", which is the only thing
      // that should ever show the not-found screen.
      (err) => setError(err.message),
    );
    return stop;
  }, [clientId, reloadKey, authKey]);

  // The client's own settings, read separately from its profile because they
  // live in a different subtree with different permissions: the profile is
  // admin-written, this is written by the club itself.
  useEffect(() => {
    if (!clientId) {
      setClientConfig({});
      return undefined;
    }
    return subscribePosClientConfig(
      clientId,
      setClientConfig,
      // Keep whatever was loaded. Falling back to {} here would repaint the
      // club in the default colours mid-shift for as long as a blip lasts.
      (err) => setError(err.message),
    );
  }, [clientId, reloadKey, authKey]);

  useEffect(() => {
    if (!clientId) {
      setBranding({});
      return undefined;
    }
    return subscribePosBranding(clientId, setBranding);
  }, [clientId, reloadKey, authKey]);

  const config = useMemo(() => ({
    ...DEFAULT_CONFIG,
    ...globalConfig,
    // LAST, so a club's own theme beats the deployment's.
    ...clientConfig,
    courtName: client?.name || '',
    // THE CLUB'S OWN LOGO WINS over the one an administrator set. The admin's
    // is what a new client starts with; this is the club changing it for
    // itself, which is the whole point of the Store Details tab.
    logoUrl: branding?.logoUrl || client?.logoUrl || '',
    fontFamily: branding?.fontFamily || '',
    // VAT comes from the PROFILE, which only an administrator can write —
    // deliberately, unlike the theme beside it. A club may repaint its own
    // till; it may not change the tax it charges. Absent on every client
    // registered before VAT existed, and posVatMode/posVatRate read that
    // absence as "no VAT" rather than as a rate to guess at.
    vatMode: client?.vatMode || DEFAULT_CONFIG.vatMode,
    vatRate: client?.vatRate ?? DEFAULT_CONFIG.vatRate,
  }), [globalConfig, clientConfig, client, branding]);

  const loading = globalLoading || (Boolean(clientId) && client === undefined);

  // Mirror the club logo into localStorage so loading screens can show it —
  // AuthGuard renders outside this provider, and the config spinner runs while
  // `config` is still the defaults, so neither can read the real logo directly.
  // Guarded on `loading`: without it this fires on mount with an empty string
  // and clears the cache at exactly the moment the spinner wants to read it.
  useEffect(() => {
    if (loading) return;
    cacheLogoUrl(config.logoUrl);
  }, [loading, config.logoUrl]);

  // Push the theme colours into a dynamically-injected stylesheet that
  // overrides css/global.css's :root custom properties.
  // The stylesheet this theme produces. Derived here rather than inside the
  // effect so the effect can depend on the RESULT.
  //
  // It used to list the theme fields by hand, and the list went stale the
  // moment a sixth colour was added: Active Button Text Color was not among
  // them, so setting it changed nothing until the page was reloaded — the
  // effect simply never re-ran. Depending on the generated CSS instead means
  // the dependency can never fall behind the fields again, and the DOM is
  // written only when the CSS genuinely differs.
  // The typeface rides along in the same injected stylesheet as the colours:
  // one <style> tag, one place the club's look is applied from.
  //
  // A CHOSEN TYPEFACE REACHES EVERY ELEMENT. The stylesheets name 'Archivo'
  // for headings and buttons, and form controls do not inherit a font at all,
  // so setting --jt-font on .app alone left most of the screen unchanged. Any
  // choice other than the default is therefore forced onto every element —
  // including modals, which are portalled outside .app. The Store Details
  // preview is exempt, since its whole job is to show a DIFFERENT face.
  const fontOverride = config.fontFamily && config.fontFamily !== POS_FONT_DEFAULT
    ? '\nbody, body * { font-family: var(--jt-font) !important; }'
      + '\nbody .pos-font-preview { font-family: var(--pos-preview-font, var(--jt-font)) !important; }'
      + '\nbody .pos-font-preview * { font-family: inherit !important; }'
    : '';
  const themeCss = useMemo(
    () => `${buildThemeCss(config)}\n:root { --jt-font: ${posFontStack(config.fontFamily)}; }${fontOverride}`,
    [config, fontOverride],
  );

  // An uploaded font has to be downloaded and registered before the family
  // named in --jt-font means anything. Until then the fallback stack shows.
  useEffect(() => {
    if (!clientId || !isPosCustomFontKey(config.fontFamily)) return;
    loadPosCustomFont(clientId, posCustomFontId(config.fontFamily))
      .catch((loadError) => console.error('Uploaded font could not be loaded:', loadError));
  }, [clientId, config.fontFamily]);

  // Push it into a dynamically-injected stylesheet that overrides
  // css/global.css's :root. The colour arithmetic lives in themeUtil; this
  // effect only owns the <style> tag.
  useEffect(() => {
    let styleTag = document.getElementById('dynamic-theme-vars');
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = 'dynamic-theme-vars';
      document.head.appendChild(styleTag);
    }
    styleTag.textContent = themeCss;
  }, [themeCss]);

  const value = useMemo(() => ({
    config,
    client: client || null,
    clientId,
    // Only ever true once the lookup has actually answered, so a slow load
    // cannot be mistaken for a mistyped address.
    clientMissing: Boolean(clientId) && client === null,
    loading,
    error,
    reloadConfig,
  }), [config, client, clientId, loading, error, reloadConfig]);

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
}

/**
 * The active config, the client it belongs to, and the load state.
 *
 * @returns {{config: object, client: ?object, clientId: ?string, clientMissing: boolean, loading: boolean, error: ?string, reloadConfig: () => void}}
 * @throws {Error} when called outside a ConfigProvider.
 */
export function useConfig() {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
}

export default ConfigContext;
